import { computed, onUnmounted, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';

export const FAVORITE_LABELS_UPDATED = 'favorite-labels:updated';

export const normalizeFavoriteLabelIds = ids =>
  [...new Set((Array.isArray(ids) ? ids : []).map(Number))].filter(
    id => Number.isInteger(id) && id > 0
  );

export const getFavoriteLabelsStorageKey = (userId, accountId) => {
  const normalizedUserId = Number(userId);
  const normalizedAccountId = Number(accountId);

  if (
    !Number.isInteger(normalizedUserId) ||
    normalizedUserId <= 0 ||
    !Number.isInteger(normalizedAccountId) ||
    normalizedAccountId <= 0
  ) {
    return '';
  }

  return `${LOCAL_STORAGE_KEYS.FAVORITE_LABELS}:${normalizedUserId}:${normalizedAccountId}`;
};

export const readFavoriteLabelIds = storageKey => {
  if (!storageKey) return null;

  const storedIds = LocalStorage.get(storageKey);
  return Array.isArray(storedIds) ? normalizeFavoriteLabelIds(storedIds) : null;
};

export const persistFavoriteLabelIds = (storageKey, ids) => {
  const normalizedIds = normalizeFavoriteLabelIds(ids);

  if (storageKey) {
    try {
      LocalStorage.set(storageKey, normalizedIds);
    } catch {
      // Keep the in-memory preference when localStorage is unavailable.
    }
  }

  return normalizedIds;
};

export function useFavoriteLabels() {
  const accountId = useMapGetter('getCurrentAccountId');
  const currentUserId = useMapGetter('getCurrentUserID');
  const { uiSettings, updateUISettings } = useUISettings();
  const localFavoriteLabelIds = ref(null);

  const storageKey = computed(() =>
    getFavoriteLabelsStorageKey(currentUserId.value, accountId.value)
  );

  const favoriteLabelsByAccount = computed(() => {
    const settings = uiSettings.value.favorite_labels;
    return settings && typeof settings === 'object' && !Array.isArray(settings)
      ? settings
      : {};
  });

  const serverFavoriteLabelIds = computed(() =>
    normalizeFavoriteLabelIds(
      favoriteLabelsByAccount.value[String(accountId.value)]
    )
  );

  watch(
    storageKey,
    key => {
      localFavoriteLabelIds.value = readFavoriteLabelIds(key);
    },
    { immediate: true, flush: 'sync' }
  );

  watch(
    [storageKey, serverFavoriteLabelIds],
    ([key, serverIds]) => {
      if (
        !key ||
        localFavoriteLabelIds.value !== null ||
        serverIds.length === 0
      ) {
        return;
      }

      localFavoriteLabelIds.value = persistFavoriteLabelIds(key, serverIds);
    },
    { immediate: true, flush: 'sync' }
  );

  const favoriteLabelIds = computed(
    () => localFavoriteLabelIds.value ?? serverFavoriteLabelIds.value
  );

  const syncFavoriteLabelIds = ({ key, ids }) => {
    if (key !== storageKey.value) return;
    localFavoriteLabelIds.value = normalizeFavoriteLabelIds(ids);
  };

  emitter.on(FAVORITE_LABELS_UPDATED, syncFavoriteLabelIds);
  onUnmounted(() => {
    emitter.off(FAVORITE_LABELS_UPDATED, syncFavoriteLabelIds);
  });

  const isLabelFavorite = labelId =>
    favoriteLabelIds.value.includes(Number(labelId));

  const toggleFavoriteLabel = labelId => {
    const id = Number(labelId);
    if (!Number.isInteger(id) || id <= 0) return false;

    const currentIds = favoriteLabelIds.value;
    const isFavorite = currentIds.includes(id);
    const nextIds = persistFavoriteLabelIds(
      storageKey.value,
      isFavorite
        ? currentIds.filter(currentId => currentId !== id)
        : [...currentIds, id]
    );

    localFavoriteLabelIds.value = nextIds;
    emitter.emit(FAVORITE_LABELS_UPDATED, {
      key: storageKey.value,
      ids: nextIds,
    });

    updateUISettings({
      favorite_labels: {
        ...favoriteLabelsByAccount.value,
        [String(accountId.value)]: nextIds,
      },
    });

    return !isFavorite;
  };

  return {
    favoriteLabelIds,
    isLabelFavorite,
    toggleFavoriteLabel,
  };
}
